import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apiRequests } from '../../shared/api/apiRequests';
import { formatImageUrl } from '../../shared/utils/formatImageUrl';
import { dateToString } from '../../shared/utils/parseDate.js';
import { Pagination } from '../../features/Pagination/index.js';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { EmptyState } from '../../shared/ui/EmptyState';
import { BriefcaseBusiness, Plus } from 'lucide-react';

const PAGE_SIZE = 10;
const FILTER_KEYS = ['q', 'categoryId', 'industryId'];

const PortfolioPage = () => {
  const [portfolio, setPortfolios] = useState({});
  const [categories, setCategories] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const categoryId = searchParams.get('categoryId') || null;
  const industryId = searchParams.get('industryId') || null;
  const [searchValue, setSearchValue] = useState(q);
  const [filterValues, setFilterValues] = useState({ categoryId, industryId });
  const [reloadKey, setReloadKey] = useState(0);
  const { page, offset, pageSize, setPage } = useUrlPagination(PAGE_SIZE);

  const hasAppliedFilters = Boolean(q || categoryId || industryId);

  // Черновики поиска и фильтров следуют за URL (назад/вперёд в браузере)
  useEffect(() => {
    setSearchValue(q);
  }, [q]);

  useEffect(() => {
    setFilterValues({ categoryId, industryId });
  }, [categoryId, industryId]);

  useEffect(() => {
    apiRequests.portfolio.getCategories(100, 0).then((res) => setCategories(res));
    apiRequests.portfolio.getIndustries(100, 0).then((res) => setIndustries(res));
  }, []);

  useEffect(() => {
    let cancelled = false;
    apiRequests.portfolio
      .get(pageSize, offset, q, categoryId, industryId)
      .then((res) => {
        if (!cancelled) setPortfolios(res);
      })
      .catch((e) => console.error(e));
    return () => {
      cancelled = true;
    };
  }, [q, categoryId, industryId, offset, pageSize, reloadKey]);

  const updateParams = (values) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      Object.entries(values).forEach(([key, value]) => {
        if (value) params.set(key, value);
        else params.delete(key);
      });
      params.delete('page');
      return params;
    });
  };

  const handleDelete = (id) => {
    apiRequests.portfolio.delete(id).then(() => {
      if (portfolio.items?.length === 1 && page > 1) setPage(page - 1);
      else setReloadKey((k) => k + 1);
    });
  };

  const handleSearch = () => updateParams({ q: searchValue.trim() });

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => updateParams(filterValues);

  const resetFilters = () => {
    setSearchValue('');
    setFilterValues({ categoryId: null, industryId: null });
    updateParams(Object.fromEntries(FILTER_KEYS.map((key) => [key, null])));
  };

  const filters = [
    {
      key: 'categoryId',
      type: 'select',
      label: 'Категория',
      value: filterValues.categoryId,
      placeholder: 'Все категории',
      options: categories?.items?.map((c) => ({ value: c.id, label: c.title })) || [],
    },
    {
      key: 'industryId',
      type: 'select',
      label: 'Индустрия',
      value: filterValues.industryId,
      placeholder: 'Все индустрии',
      options: industries?.items?.map((i) => ({ value: i.id, label: i.title })) || [],
    },
  ];

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск портфолио..."
        filters={filters}
        onFilterChange={handleFilterChange}
        onApplyFilters={applyFilters}
        onResetFilters={resetFilters}
        createLink="/portfolio/create"
      />

      <div className="mb-3">
        {portfolio.items?.length === 0 ? (
          hasAppliedFilters ? (
            <EmptyState
              icon={BriefcaseBusiness}
              variant="search"
              title="Кейсы не найдены"
              description="Ни один кейс не подходит под запрос и выбранные категорию и индустрию. Попробуйте изменить формулировку или сбросить фильтры."
            />
          ) : (
            <EmptyState
              icon={BriefcaseBusiness}
              title="В портфолио пока пусто"
              description="Добавьте первый кейс: обложку, описание, контент страницы и переводы можно заполнить на одной странице."
              action={{ label: 'Добавить кейс', to: '/portfolio/create', icon: Plus }}
            />
          )
        ) : (
          portfolio.items?.map((item) => (
            <EntityCard
              key={item.id}
              image={formatImageUrl(item.imgPath)}
              title={item.title}
              fields={[
                { label: 'Категория', value: item.category?.title },
                { label: 'Создано', value: dateToString(item.createdAt) },
              ]}
              editLink={`/portfolio/${item.id}`}
              onDelete={() => handleDelete(item.id)}
            />
          ))
        )}
      </div>

      <Pagination
        page={page}
        total={portfolio.totalCount}
        pageSize={pageSize}
        onPageChange={setPage}
      />
    </>
  );
};

export default PortfolioPage;
